import { Inject, Injectable, Optional } from '@angular/core';
import { CUSTOMERENVIRONMENT, WINDOW } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { AccountInfo, AuthenticationResult, EndSessionRequest, InteractionRequiredAuthError, PopupRequest, PublicClientApplication, RedirectRequest, SilentRequest, SsoSilentRequest } from '@azure/msal-browser';
import { BaseAuthRequest, ClientAuthErrorMessage, CommonAuthorizationUrlRequest } from '@azure/msal-common';
import { Environment } from '@environments/interface.environment';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, map, tap } from 'rxjs/operators';
import { Platform } from '@angular/cdk/platform';
import { MsteamsService } from '@app/services/msteams.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public useRedirect: boolean;

  public readonly MSALObj: PublicClientApplication;

  public accountInfo: AccountInfo;

  public storedAccountInfo: AccountInfo;

  public loginRequest: Partial<CommonAuthorizationUrlRequest>;

  private protectedResourceEndpoints: Map<string, string[]>;

  public idTokenClaims = new Map<string, any>();

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment, @Inject(WINDOW) private window: Window, private platform: Platform, @Optional() private msteamsService?: MsteamsService) {
    // If IE, recommendation is that you sign-in using Redirect APIs (https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/samples/msal-browser-samples/TypescriptTestApp2.0/src/index.ts#L7)
    // If iOS we have to force redirect otherwise safari popup wont work in a good way
    // If we are in teams we set this to redirect because we have to open the special MS Teams auth popup manually and trigger a redirect in that popup.
    // If the customer has configured to use redirect we set this to true as well
    this.useRedirect = Utilities.detectIE(this.window.navigator.userAgent) > -1 ||
      (this.platform.IOS && Utilities.isSafari(this.window)) ||
      Utilities.isInTeamsApp(this.window) ||
      Utilities.inTeamsAuthPopupRedirect(this.window) ||
      this.environment.msalConfig.useRedirect;

    // This should be set if IE according to MSAL docs.
    this.environment.msalConfig.cache = { ...{ storeAuthStateInCookie: Utilities.detectIE(this.window.navigator.userAgent) > -1 }, ...this.environment.msalConfig.cache };

    if (Utilities.isElectron(this.window) || Utilities.isInTeamsApp(this.window) || Utilities.inTeamsAuthPopupRedirect(this.window) || this.environment.featureSettings.coreIntranetEnabled) {
      // Set this to localstorage to help minimize the needs for logging in multiple times since each tab change will cause a new session.
      // This also needs to be set to localStorage because the special MS teams auth popup needs to store the info in a shared storage between the parent window since we have no way of force setting auth properties in MSAL.
      this.environment.msalConfig.cache.cacheLocation = 'localStorage';
    }

    if (!this.environment.msalConfig.auth.redirectUri) {
      this.environment.msalConfig.auth.redirectUri = this.window.location.origin;
    }

    if (Utilities.inTeamsAuthPopupRedirect(this.window)) {
      // Because when we are within this special auth popup we want to get rid of it as quick as possible and tell it to close in the initializer we dont want to trigger any extra navigations.
      this.environment.msalConfig.auth.navigateToLoginRequestUrl = false;
    }

    this.MSALObj = new PublicClientApplication(this.environment.msalConfig);

    this.loginRequest = {
      scopes: ['openid', 'profile', 'offline_access', `${this.environment.msalConfig.auth.clientId}/.default`],
      extraQueryParameters: this.environment.msalConfig.extraQueryParameters,
      loginHint: this.environment.msalConfig.loginHint,
      domainHint: this.environment.msalConfig.domainHint,
    };

    // If the user has logged in before we check if there is some previous account info saved, this will check the browser storage.
    // If there is one account saved we use that info to prime the login request and then we can check that info and try with ssoSilent first or try to speed up the login with prompt=none
    const storedAccountInfos = this.MSALObj.getAllAccounts();
    if (storedAccountInfos) {
      if (storedAccountInfos.length === 1) {
        this.storedAccountInfo = storedAccountInfos[0];
        this.accountInfo = storedAccountInfos[0];
        this.loginRequest = { ...this.loginRequest, ...{ account: this.storedAccountInfo, prompt: 'none' } };
      }
      if (storedAccountInfos.length > 1) {
        this.loginRequest = { ...this.loginRequest, ...{ prompt: 'select_account' } };
      }
    }

    this.protectedResourceEndpoints = new Map<string, string[]>(this.environment.msalConfig.protectedResourceScopeMap);
  }

  handleRedirect(): Observable<AuthenticationResult> {
    return from(this.MSALObj.handleRedirectPromise())
      .pipe(
        tap((authenticationResult: AuthenticationResult) => {
          this.setFields(authenticationResult);
        })
      );
  }

  private setFields(authenticationResult: AuthenticationResult) {
    if (authenticationResult) {
      Object.keys(authenticationResult.idTokenClaims)
        .forEach((key) => {
          this.idTokenClaims.set(key, authenticationResult.idTokenClaims[key]);
        });
      if (!this.accountInfo) {
        this.accountInfo = authenticationResult.account;
      }
    }
  }

  loginRedirect(request?: RedirectRequest): Observable<void> {
    return from(this.MSALObj.loginRedirect(request));
  }

  loginPopup(request?: PopupRequest): Observable<AuthenticationResult> {
    return from(this.MSALObj.loginPopup(request))
      .pipe(
        tap((authResult: AuthenticationResult) => {
          this.setFields(authResult);
        })
      );
  }

  logOut(request?: EndSessionRequest): Observable<void> {
    return from(this.MSALObj.logout(request));
  }

  acquireTokenSilent(request: SilentRequest): Observable<AuthenticationResult> {
    request.account = this.accountInfo;
    return from(this.MSALObj.acquireTokenSilent(request));
  }

  acquireTokenSilentWithFallBackToPopup(request: SilentRequest, fallbackRequest: BaseAuthRequest): Observable<AuthenticationResult> {
    return this.acquireTokenSilent(request)
      .pipe(
        catchError((err) => {
          if (err instanceof InteractionRequiredAuthError) {
            return this.acquireTokenPopup(fallbackRequest);
          }
          return throwError(err);
        }));
  }

  acquireTokenPopup(request: PopupRequest): Observable<AuthenticationResult> {
    return from(this.MSALObj.acquireTokenPopup(request));
  }

  acquireTokenRedirect(request: RedirectRequest): Observable<AuthenticationResult> {
    request.account = this.accountInfo;
    return this.handleRedirect()
      .pipe(
        mergeMap(authenticationResult => {
          if (authenticationResult) {
            return of(authenticationResult);
          } else {
            return from(this.MSALObj.acquireTokenRedirect(request))
              .pipe(
                map(() => null)
              );
          }
        })
      );
  }

  acquireTokenSilentWithFallBackToRedirect(request: SilentRequest, fallbackRequest: RedirectRequest): Observable<AuthenticationResult> {
    return this.acquireTokenSilent(request)
      .pipe(
        catchError((err) => {
          if (err instanceof InteractionRequiredAuthError) {
            if ((Utilities.isInTeamsApp(this.window) && this.msteamsService) || this.msteamsService?.isTeamsInitialized) {
              return this.acquireTokenUsingTeamsAuth(fallbackRequest);
            } else {
              return this.acquireTokenRedirect(fallbackRequest);
            }
          }
          if (err?.errorCode === ClientAuthErrorMessage.noTokensFoundError.code) {
            if ((Utilities.isInTeamsApp(this.window) && this.msteamsService) || this.msteamsService?.isTeamsInitialized) {
              return this.acquireTokenUsingTeamsAuth(fallbackRequest);
            } else {
              return this.acquireTokenRedirect((this.loginRequest as RedirectRequest));
            }
          }
          return throwError(err);
        }));
  }

  private acquireTokenUsingTeamsAuth(fallbackRequest: RedirectRequest) {
    return this.msteamsService.initMsTeams()
      .pipe(
        mergeMap(() => {
          return this.msteamsService.authenticate(fallbackRequest.scopes.join(','));
        }),
        map(result => {
          return JSON.parse(result) as AuthenticationResult;
        }));
  }

  ssoSilent(request: SsoSilentRequest): Observable<AuthenticationResult> {
    return from(this.MSALObj.ssoSilent(request))
      .pipe(
        tap((authenticationResult: AuthenticationResult) => {
          this.setFields(authenticationResult);
        })
      );
  }

  getScopesForEndpoint(url: string): BaseAuthRequest {
    let request: BaseAuthRequest;

    for (const [resource, scopes] of this.protectedResourceEndpoints) {
      if (url.indexOf(resource) > -1) {
        request = { scopes: scopes, authority: this.environment.msalConfig.auth.authority, correlationId: '' };
        break;
      }
    }
    return request;
  }
}
